/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreApiService } from '../core-api-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { deleteByUid } from '../fn/iam/delete-by-uid';
import { DeleteByUid$Params } from '../fn/iam/delete-by-uid';
import { getAllUsers } from '../fn/iam/get-all-users';
import { GetAllUsers$Params } from '../fn/iam/get-all-users';
import { getByUid } from '../fn/iam/get-by-uid';
import { GetByUid$Params } from '../fn/iam/get-by-uid';
import { getProfile } from '../fn/iam/get-profile';
import { GetProfile$Params } from '../fn/iam/get-profile';
import { PageDto } from '../models/page-dto';
import { postUser } from '../fn/iam/post-user';
import { PostUser$Params } from '../fn/iam/post-user';
import { UserDto } from '../models/user-dto';

@Injectable({ providedIn: 'root' })
export class IamAPI extends CoreApiService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllUsers()` */
  static readonly GetAllUsersPath = '/iam';

  /**
   * Get all users from the system.
   *
   * Paginated call to retrieve all users from the system.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsers$Response(params?: GetAllUsers$Params, context?: HttpContext): Observable<StrictHttpResponse<PageDto>> {
    return getAllUsers(this.http, this.rootUrl, params, context);
  }

  /**
   * Get all users from the system.
   *
   * Paginated call to retrieve all users from the system.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsers(params?: GetAllUsers$Params, context?: HttpContext): Observable<PageDto> {
    return this.getAllUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<PageDto>): PageDto => r.body)
    );
  }

  /** Path part for operation `postUser()` */
  static readonly PostUserPath = '/iam';

  /**
   * Creates a new user if necessary.
   *
   * Creates a new user in the system from the session token. If the user already exists, nothing happens
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  postUser$Response(params?: PostUser$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postUser(this.http, this.rootUrl, params, context);
  }

  /**
   * Creates a new user if necessary.
   *
   * Creates a new user in the system from the session token. If the user already exists, nothing happens
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postUser(params?: PostUser$Params, context?: HttpContext): Observable<void> {
    return this.postUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getProfile()` */
  static readonly GetProfilePath = '/iam/profile';

  /**
   * Return user information of the logged in user.
   *
   * Returns the user information from the logged in user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProfile()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProfile$Response(params?: GetProfile$Params, context?: HttpContext): Observable<StrictHttpResponse<UserDto>> {
    return getProfile(this.http, this.rootUrl, params, context);
  }

  /**
   * Return user information of the logged in user.
   *
   * Returns the user information from the logged in user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProfile$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProfile(params?: GetProfile$Params, context?: HttpContext): Observable<UserDto> {
    return this.getProfile$Response(params, context).pipe(
      map((r: StrictHttpResponse<UserDto>): UserDto => r.body)
    );
  }

  /** Path part for operation `getByUid()` */
  static readonly GetByUidPath = '/iam/{uid}';

  /**
   * Get a specific user from the system.
   *
   * Retrieves the user data for a given user id (uid)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getByUid()` instead.
   *
   * This method doesn't expect any request body.
   */
  getByUid$Response(params: GetByUid$Params, context?: HttpContext): Observable<StrictHttpResponse<UserDto>> {
    return getByUid(this.http, this.rootUrl, params, context);
  }

  /**
   * Get a specific user from the system.
   *
   * Retrieves the user data for a given user id (uid)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getByUid$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getByUid(params: GetByUid$Params, context?: HttpContext): Observable<UserDto> {
    return this.getByUid$Response(params, context).pipe(
      map((r: StrictHttpResponse<UserDto>): UserDto => r.body)
    );
  }

  /** Path part for operation `deleteByUid()` */
  static readonly DeleteByUidPath = '/iam/{uid}';

  /**
   * Delete a user from the system.
   *
   * Deletes a user from the system. User may stay existing in the OIDC provider, but will not be able to login
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteByUid()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteByUid$Response(params: DeleteByUid$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteByUid(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete a user from the system.
   *
   * Deletes a user from the system. User may stay existing in the OIDC provider, but will not be able to login
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteByUid$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteByUid(params: DeleteByUid$Params, context?: HttpContext): Observable<void> {
    return this.deleteByUid$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
